import { Record } from "@bloomberg/record-tuple-polyfill";
import Layout from "components/Layout";
import { IVisualizerProps, Visualizer } from "components/Visualizer";
import { FeedbackContextProvider } from "components/Visualizer/VisualizerFeedbackForm";
import { readCMSDataServerSide } from "helpers/api";
import { filterOutNulls } from "helpers/utils";
import type { GetServerSidePropsContext, GetServerSidePropsResult } from "next";
import { EmtekPageProps } from "pages/[[...slug]]";
import { EmtekClientProvider } from "queryClient";

import { ToolboxBlock } from "./Toolbox";

interface RulesetApiResponse extends Pick<IVisualizerProps, "steps"> {
    id: number;
}

export interface PageProps
    extends EmtekPageProps<{
        body: ToolboxBlock[];
        show_feedback_links: boolean;
    }> {
    visualizerData: RulesetApiResponse;
    referID: string;
}

export default function VisualizerPage({
    wagtail,
    menus,
    visualizerData,
    referID,
}: PageProps) {
    if (!visualizerData) {
        return (
            <Layout metadata={wagtail} menus={menus}>
                <p>Error loading visualizer data</p>
            </Layout>
        );
    }

    return (
        <EmtekClientProvider>
            <FeedbackContextProvider
                showLink={wagtail.show_feedback_links}
                ruleset={visualizerData.id}
            >
                <Layout metadata={wagtail} menus={menus}>
                    <Visualizer
                        steps={visualizerData.steps}
                        referID={referID}
                    />
                </Layout>
            </FeedbackContextProvider>
        </EmtekClientProvider>
    );
}

export async function fetchData(
    context: GetServerSidePropsContext,
    wagtailPageProps: PageProps,
    apiResponse: Response,
) {
    const url = new URL(
        context.resolvedUrl,
        `https://${context.req.headers.host}`,
    );
    const query = url.searchParams;

    const referID = query.get("referID");

    if (!referID) {
        return {
            redirect: {
                permanent: false,
                destination: "/",
            },
        } as GetServerSidePropsResult<any>;
    }
    const visualizerData: RulesetApiResponse = await readCMSDataServerSide(
        `api/rulesets/referring_productpage/${referID}`,
        {
            context: context,
            search: query,
        },
    );

    const expectedRuleParams = Record(
        Object.fromEntries(
            visualizerData.steps
                .flatMap((step) =>
                    step.rules.flatMap((rule) => [rule, rule.filter_rule]),
                )
                .map((rule) => {
                    if (!rule || !rule.selected) return null;
                    return [`rule-${rule.id}`, `${rule.selected}`];
                })
                .filter(filterOutNulls),
        ),
    );

    const actualRuleParams = Record(
        Object.fromEntries(
            [...query].filter(([key, _value]) => key.startsWith("rule-")),
        ),
    );
    if (expectedRuleParams !== actualRuleParams) {
        for (let key of [...query.keys()].filter(
            (k) => k.startsWith("attr-") || k.startsWith("rule-"),
        ))
            query.delete(key);
        for (let [key, value] of Object.entries(expectedRuleParams))
            query.set(key, value as string);
        return {
            redirect: {
                permanent: false,
                destination: `${url.pathname}?${query.toString()}`,
            },
        } as GetServerSidePropsResult<any>;
    }

    const props: PageProps = {
        ...wagtailPageProps,
        visualizerData,
        referID,
    };

    return {
        props: props,
        apiResponse: apiResponse,
    };
}
